import { useState, type ReactNode } from 'react';
import { ChevronRight, Folder, Lock, type LucideIcon } from 'lucide-react';
import type { PropertyFileTreeNode } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { collectFilesDeep } from './akteBrowserNav';
import { folderDisplayName } from './akteFolderNames';
import type { useAkteDragMove } from './useAkteDragMove';

interface Props {
  node: PropertyFileTreeNode;
  /** Nur für Top-Kategorien gesetzt: eigenes Icon + Titel. */
  icon?: LucideIcon;
  title?: string;
  depth: number;
  currentPath: string;
  onNavigate: (path: string) => void;
  /** Kontextmenü-Renderer (Custom-Ordner) — von C2 injiziert. */
  renderMenu?: (node: PropertyFileTreeNode) => ReactNode;
  /** Drop-Ziel-Handler (Drag-Move auf diesen Ordner). */
  drop?: ReturnType<typeof useAkteDragMove>['dropProps'];
}

/** Enthält der Teilbaum ausschließlich gesperrte Belege (≥1 Datei)? → Lock-Indikator. */
function isAllLocked(node: PropertyFileTreeNode): boolean {
  const files = collectFilesDeep(node);
  return files.length > 0 && files.every((f) => f.locked);
}

function autoOpen(name: string, depth: number): boolean {
  const now = new Date();
  const months = [
    'Januar',
    'Februar',
    'Maerz',
    'April',
    'Mai',
    'Juni',
    'Juli',
    'August',
    'September',
    'Oktober',
    'November',
    'Dezember',
  ];
  // Lokalzeit: die Jahr/Monat-Ordner leiten sich aus dem (lokal gewählten) Beleg-Datum ab —
  // an Datumsgrenzen darf UTC nicht auf den nächsten Monat/Jahr springen.
  if (depth === 1 && name === String(now.getFullYear())) return true;
  if (depth === 2 && name === months[now.getMonth()]) return true;
  if (depth === 1 && name === 'Aktiv') return true;
  return false;
}

export function AkteBrowserTreeNode({
  node,
  icon,
  title,
  depth,
  currentPath,
  onNavigate,
  renderMenu,
  drop,
}: Props) {
  const [open, setOpen] = useState(() => autoOpen(node.name, depth));
  const NodeIcon = icon ?? Folder;
  const label = title ?? folderDisplayName(node.path);
  const hasChildren = node.folders.length > 0;
  const empty = node.descendantFileCount === 0;
  const active = node.path === currentPath;
  const locked = isAllLocked(node);
  // Spring-Loading: einen zugeklappten Ordner mit Kindern beim Ziehen aufklappen.
  const dropTarget = drop?.(node.path, hasChildren && !open ? () => setOpen(true) : undefined);

  return (
    <div>
      <div
        {...dropTarget?.props}
        className={cn(
          'group flex items-center rounded-md pr-1 transition-colors',
          dropTarget?.active
            ? 'bg-sealwax/15 border-sealwax border-l-[3px]'
            : active
              ? 'bg-sealwax/10 border-sealwax text-sealwax border-l-[3px]'
              : 'hover:bg-card-alt/60 border-l-[3px] border-transparent',
        )}
        style={{ paddingLeft: `${depth * 12}px` }}
      >
        {/* Ohne Kinder gab es hier einen unsichtbaren, aber fokussierbaren Button ohne Label. */}
        {hasChildren ? (
          <button
            type="button"
            aria-label={open ? `${label} zuklappen` : `${label} aufklappen`}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="text-ink-soft hover:text-ink flex h-11 w-11 shrink-0 items-center justify-center"
          >
            <ChevronRight size={16} className={cn('transition-transform', open && 'rotate-90')} />
          </button>
        ) : (
          <span className="h-11 w-11 shrink-0" aria-hidden />
        )}
        <button
          type="button"
          onClick={() => onNavigate(node.path)}
          className={cn(
            'flex min-h-11 min-w-0 flex-1 items-center gap-2.5 py-2 text-left text-[15px]',
            !active && (empty ? 'text-ink-faint' : 'text-ink'),
          )}
        >
          <NodeIcon size={18} className="shrink-0 opacity-80" />
          <span className="truncate font-serif font-medium">{label}</span>
          {locked && <Lock size={13} className="text-ink-faint shrink-0" aria-label="Gesperrt" />}
        </button>
        {empty ? (
          <span className="text-ink-faint shrink-0 pr-1 text-xs italic">leer</span>
        ) : (
          <span className="bg-card text-ink-soft shrink-0 rounded px-1.5 py-0.5 text-xs tabular-nums">
            {node.descendantFileCount}
          </span>
        )}
        {renderMenu?.(node)}
      </div>

      {open &&
        node.folders.map((sub) => (
          <AkteBrowserTreeNode
            key={sub.path}
            node={sub}
            depth={depth + 1}
            currentPath={currentPath}
            onNavigate={onNavigate}
            renderMenu={renderMenu}
            drop={drop}
          />
        ))}
    </div>
  );
}
